<template>
  <ion-page
    :class="{
      'pt-[120px]': segmentPage,
      'pt-[76px]': !segmentPage,
    }"
  >
    <ion-tabs class="bg-smoke">
      <ion-router-outlet />
      <ion-tab-bar
        v-if="!noNavigationPages.includes(routeName) && !isDesktop"
        slot="bottom"
        class="h-64 items-end bg-transparent relative bg-smoke"
      >
        <ion-tab-button
          tab="dashboard"
          href="/dashboard"
          class="h-64 bg-smoke"
        >
          <ion-icon
            aria-hidden="true"
            :icon="activeTabName === 'dashboard' ? home : homeOutline"
            class="w-24 h-24"
            :class="{
              'text-primary-default': activeTabName === 'dashboard',
            }"
          />
          <ion-label class="text-xs">Hem</ion-label>
        </ion-tab-button>
        <ion-tab-button
          v-if="!groupsHidden"
          tab="groups"
          href="/groups"
          class="h-64 bg-smoke"
        >
          <ion-icon
            aria-hidden="true"
            :icon="activeTabName === 'groups' ? chatbubbles : chatbubblesOutline"
            class="w-24 h-24"
            :class="{
              'text-primary-default': activeTabName === 'groups',
            }"
          />
          <ion-label class="text-xs">Grupper</ion-label>
          <div v-if="groupBadge" class="absolute w-16 h-16 bg-orange text-white rounded-full top-4 left-[55%] flex items-center justify-center">
            {{ groupBadge }}
          </div>
        </ion-tab-button>
        <div
          v-if="!reportHidden"
          class="flex flex-col items-center flex-1 bg-smoke h-64 justify-center cursor-pointer hover:text-primary-default"
          @click="startReport"
        >
          <div class="w-24 h-24 flex justify-center">
            <div class="absolute top-[-16px] w-[56px] h-[56px] rounded-full flex items-center justify-center bg-smoke">
              <div
                class="w-32 h-32 rounded-full relative flex justify-center items-center"
                :class="{
                  'bg-transparent': !darkText,
                  'bg-black': darkText,
                }"
              >
                <ion-icon
                  aria-hidden="true"
                  :icon="addCircle"
                  class="w-44 h-44 text-primary-default absolute"
                />
              </div>
            </div>
          </div>
          <ion-label class="text-xs font-medium">Anmäl</ion-label>
        </div>
        <ion-tab-button
          v-if="!checklistsHidden"
          tab="checklists"
          href="/checklists"
          class="h-64 bg-smoke"
        >
          <ion-icon
            aria-hidden="true"
            :icon="activeTabName === 'checklists' ? list : listOutline"
            class="w-24 h-24"
            :class="{
              'text-primary-default': activeTabName === 'checklists',
            }"
          />
          <ion-label class="text-xs">Tips & råd</ion-label>
          <div v-if="checklistBadge" class="absolute w-16 h-16 bg-orange text-white rounded-full top-4 left-[55%] flex items-center justify-center">
            {{ checklistBadge }}
          </div>
        </ion-tab-button>
        <ion-tab-button
          v-if="!warningsHidden"
          tab="warnings"
          href="/warnings"
          class="h-64 bg-smoke"
        >
          <ion-icon
            aria-hidden="true"
            :icon="activeTabName === 'warnings' ? warning : warningOutline"
            class="w-24 h-24"
            :class="{
              'text-primary-default': activeTabName === 'warnings',
            }"
          />
          <ion-label class="text-xs">Varningar</ion-label>
          <div v-if="warningsBadge" class="absolute w-16 h-16 bg-orange text-white rounded-full top-4 left-[55%] flex items-center justify-center">
            {{ warningsBadge }}
          </div>
        </ion-tab-button>
      </ion-tab-bar>
      <div class="safe-area-spacing bg-smoke z-10" />
    </ion-tabs>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonIcon,
  IonLabel,
  IonPage,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  useIonRouter
} from '@ionic/vue';
import {
  addCircle,
  chatbubbles,
  chatbubblesOutline,
  home,
  homeOutline,
  list,
  listOutline,
  warning,
  warningOutline
} from 'ionicons/icons';
import { NotificationType, useUiStore } from '@/store/uiStore';
import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted } from 'vue';
import { useIncidentStore } from '@/store/incident';
import { useWarningsStore } from '@/store/warnings';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store/user';
import { useFeatureVisibility } from '@/composeables/useFeatureVisibility';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

const incidentStore = useIncidentStore();
const warningStore = useWarningsStore();
const userStore = useUserStore();
const uiStore = useUiStore();
const router = useRouter();
const route = useRoute();
const ionRouter = useIonRouter();

const { checklistBadge, groupBadge, warningsBadge, appIsActive, isDesktop } = storeToRefs(uiStore);
const { darkText } = storeToRefs(userStore);
const { reportHidden, groupsHidden, checklistsHidden, warningsHidden } = useFeatureVisibility();

onMounted(async() => {
  uiStore.loadingDashboard(true);
  await loadUserData();
  await Promise.all([
    incidentStore.fetchTemplates(),
    incidentStore.fetchUserIncidents(),
    warningStore.fetchAllWarnings(),
    warningStore.fetchWarningChecklist(),
    registerNotificationNavigations(),
  ]);
  uiStore.loadingDashboard(false);
});
const startReport = () => {
  incidentStore.openIncidentModal();
};

const routeName = computed(() => router.currentRoute.value.name as string);
const segmentPage = computed(() => {
  const routeName = router.currentRoute.value.name as string;
  return ['checklists', 'groups', 'warnings', 'sign', 'showGroup'].includes(routeName);
});

const activeTabName = computed(() => {
  return route.matched[1] && route.matched[1].path.split('/')[1] || '';
});

const noNavigationPages = ['showGroupComments', 'showGroup'];

const registerNotificationNavigations = async() => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  await PushNotifications.addListener('pushNotificationActionPerformed', async(notification) => {
    const notificationData = notification.notification.data;
    const id = notificationData.id;

    await userStore.fetchUser();
    const actions = {
      [NotificationType.Regular]: () => {
        ionRouter.push({ name: 'singleWarning', params: { id } });
      },
      [NotificationType.Security]: () => {
        ionRouter.push({ name: 'singleSecurityWarning', params: { id } });
      },
      [NotificationType.Police]: () => {
        ionRouter.push({ name: 'warnings', query: { tab: 'police' } });
      },
      [NotificationType.Checklist]: () => {
        ionRouter.push({ name: 'checklistGroup', params: { id } });
      },
      [NotificationType.GroupPostPublished]: () => {
        ionRouter.push({ name: 'showGroupComments', params: { id } });
      },
      [NotificationType.GroupPostComment]: () => {
        ionRouter.push({ name: 'showGroupComments', params: { id } });
      },
      // Both like types carry the post id, so a liked comment opens the thread
      // it belongs to.
      [NotificationType.GroupPostLike]: () => {
        ionRouter.push({ name: 'showGroupComments', params: { id } });
      },
      [NotificationType.GroupPostCommentLike]: () => {
        ionRouter.push({ name: 'showGroupComments', params: { id } });
      },
      [NotificationType.EmergencyReplyUpdated]: () => {
        ionRouter.push({ name: 'showGroupComments', params: { id } });
      },
      [NotificationType.GroupChecklistNew]: () => {
        ionRouter.push({ name: 'showGroup', params: { id }, query: { tab: 'checklist' }  });
      },
      [NotificationType.GroupMembershipInvite]: () => {
        ionRouter.push({ name: 'allGroups', query: { openModal: 'invites' } });
      },
      [NotificationType.GroupMembershipRequest]: () => {
        ionRouter.push({ name: 'allGroups', query: { openModal: 'requests' } });
      },
      [NotificationType.GroupMembershipMember]: () => {
        ionRouter.push({ name: 'showGroup', params: { id } });
      },
    };

    if (!actions[notificationData.type]) {
      return;
    }

    const waitForAppIsActive = (): Promise<void> => {
      return new Promise<void>((resolve) => {
        const intervalId = setInterval(() => {
          if (appIsActive) {
            clearInterval(intervalId); // Stop the interval
            resolve();
          }
        }, 100); // Check every 100 milliseconds
      });
    };
    
    waitForAppIsActive().then(() => {
      actions[notificationData.type]();
    });
  });
};

const loadUserData = async() => {
  if (userStore.currentUser) {
    await Promise.all([
      userStore.fetchEmergencies(),
      userStore.fetchMyChecklists(),
      userStore.fetchUserGroups(),
      userStore.fetchUserNotificationSettings(),
    ]);
  }
};

</script>
<style>
.safe-area-spacing {
  height: var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px));
}

ion-tab-bar {
  contain: none !important;
}
</style>
